import React, { useState } from 'react';
import { Clock, Check, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { ProfileAvatar, UserProfilePopover } from '../ui';
import SuspendedMessageBubble from './SuspendedMessageBubble';
import type { ChatMessage } from '../../store/chatStore';

interface MessageBubbleProps {
  message: ChatMessage;
  isGrouped?: boolean;
  isGroupLeader?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isGrouped = false, isGroupLeader = true }) => {
  const { user } = useAuthStore();
  const isOwnMessage = user?.id.toString() === message.userId;
  const [gifLoadError, setGifLoadError] = useState(false);

  // Check if the message sender is suspended
  const isSuspended = message.userSuspendedAt !== null && message.userSuspendedAt !== undefined;

  // If user is suspended, show suspended message bubble
  if (isSuspended) {
    return <SuspendedMessageBubble timestamp={message.createdAt} isGrouped={isGrouped} />;
  }

  const formatTime = (dateString: string) => {
    const messageDate = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - messageDate.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    // Show relative time for recent messages in Dutch
    if (diffInMinutes < 1) {
      return 'recent';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m geleden`;
    } else if (diffInHours < 24) {
      return `${diffInHours}u geleden`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d geleden`;
    } else {
      // For older messages, show the actual time in Dutch format
      return messageDate.toLocaleTimeString('nl-NL', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  const formatFullTime = (dateString: string) => {
    const messageDate = new Date(dateString);
    return messageDate.toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const renderMessageContent = (message: ChatMessage, _isOwnMessage: boolean) => {
    const messageType = message.messageType || 'TEXT';

    switch (messageType) {
      case 'GIF':
      case 'IMAGE': {
        const isPhoto = messageType === 'IMAGE';
        const media = (
          <div className="relative rounded-lg overflow-hidden bg-white/[.06] max-w-xs">
            {!gifLoadError ? (
              <img
                src={message.gifUrl || ''}
                alt={isPhoto ? 'Foto' : 'GIF'}
                className="w-full h-auto max-h-64 object-cover"
                onError={() => setGifLoadError(true)}
                loading="lazy"
              />
            ) : (
              <div className="flex items-center justify-center h-32 bg-white/[.05]">
                <div className="text-center">
                  <ImageIcon className="h-8 w-8 text-fg-faint mx-auto mb-2" />
                  <p className="text-xs text-fg-muted">
                    {isPhoto ? 'Foto kon niet geladen worden' : 'GIF kon niet geladen worden'}
                  </p>
                </div>
              </div>
            )}
          </div>
        );

        return (
          <div className="space-y-2">
            {/* Photos open full-size in a new tab; GIFs animate in place */}
            {isPhoto && !gifLoadError ? (
              <a
                href={message.gifUrl || ''}
                target="_blank"
                rel="noopener noreferrer"
                className="block cursor-zoom-in"
                title="Bekijk op volledige grootte"
              >
                {media}
              </a>
            ) : (
              media
            )}

            {/* Caption if provided */}
            {message.content && message.content.trim() && (
              <p className="text-sm whitespace-pre-wrap break-words break-all leading-relaxed">
                {message.content}
              </p>
            )}
          </div>
        );
      }

      case 'EMOJI':
      case 'TEXT':
      default:
        return (
          <p className="text-[15px] font-medium whitespace-pre-wrap break-words break-all leading-relaxed">
            {message.content}
          </p>
        );
    }
  };

  if (message.type === 'system') {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-white/[.05] text-fg-faint font-mono text-xs px-3 py-1 rounded-full">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} ${isGrouped ? 'mb-1' : 'mb-4'}`}>
      <div className={`flex max-w-xs lg:max-w-md min-w-0 items-end ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar - only show for group leaders */}
        {isGroupLeader && (
          <div className={`flex-shrink-0 ${isOwnMessage ? 'ml-2' : 'mr-2'}`}>
            <UserProfilePopover
              userId={parseInt(message.userId)}
              placement={isOwnMessage ? "left" : "right"}
              trigger={
                <div className="cursor-pointer">
                  <ProfileAvatar
                    user={{
                      username: message.username,
                      profilePhoto: message.userProfilePhoto
                    }}
                    size="sm"
                  />
                </div>
              }
            />
          </div>
        )}

        {/* Spacer for grouped messages without avatar */}
        {!isGroupLeader && (
          <div className={`flex-shrink-0 ${isOwnMessage ? 'ml-2' : 'mr-2'} w-8`} />
        )}

        {/* Message content */}
        <div className={`flex flex-col min-w-0 ${isOwnMessage ? 'items-end' : 'items-start'}`}>
          {/* Username and time - only show for group leaders */}
          {!isOwnMessage && isGroupLeader && (
            <div className="flex items-center gap-2 mb-1 ml-1">
              <span className="text-[11px] font-medium text-fg-meta">
                {message.username}
              </span>
              <span
                className="text-[11px] text-fg-meta cursor-help"
                title={formatFullTime(message.createdAt)}
              >
                {formatTime(message.createdAt)}
              </span>
            </div>
          )}

          {/* Message bubble */}
          <div
            className={`px-4 py-2.5 min-w-0 max-w-full ${
              isOwnMessage
                ? 'rounded-[16px] rounded-br-[4px] bg-bubble-own text-white'
                : 'rounded-[16px] rounded-bl-[4px] border border-white/[.08] bg-white/[.06] text-fg-soft'
            }`}
          >
            {renderMessageContent(message, isOwnMessage)}
          </div>

          {/* Time and status for own messages - only show for group leaders */}
          {isOwnMessage && isGroupLeader && (
            <div className="flex items-center gap-1 mt-1">
              <span
                className="text-[11px] text-fg-meta cursor-help"
                title={formatFullTime(message.createdAt)}
              >
                {formatTime(message.createdAt)}
              </span>
              {/* Message status indicator */}
              {message.status === 'sending' && (
                <Clock className="h-3 w-3 text-fg-faint" />
              )}
              {message.status === 'sent' && (
                <Check className="h-3 w-3 text-neon-lime" />
              )}
              {message.status === 'failed' && (
                <AlertCircle className="h-3 w-3 text-neon-pink-soft" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;